// Localization runtime, the PWA side of TrailBivvy/Localization/L10n.swift (TrailBivvyIOS): `t(key, …args)` is
// `String(localized: key, defaultValue: …)` with the iOS catalog's String(format:) placeholders, over the tables that
// scripts/build-l10n.mjs extracts from Localizable.xcstrings (+ PWA-only strings from src/i18n/extra/).
//
// - English is bundled (always available, offline, before any download); one other language is loaded on demand
//   (`import.meta.glob`, one lazy chunk per language), so only the active language is downloaded.
// - `locale` is the UI language; `formatLocale` adds the user's region for numbers and dates, as iOS `Locale.current`
//   combines the app language with the device region ("de" + CH → 1’240 m).
// - Reading `t()` inside a component subscribes it to `locale`, so a language change re-renders every screen.

import { batch, computed, signal } from '@preact/signals';
import en from './locales/en.json';
import type { FormatArg, L10nArgs, L10nKey } from './keys';
import { supportedLanguages, type LanguageCode } from './languages';

export { supportedLanguages, sourceLanguage, l10nSource, type LanguageCode, type LanguageInfo } from './languages';
export type { FormatArg, L10nArgs, L10nKey } from './keys';

/** A flat string table: key → text with iOS placeholders (%@, %1$@, %lld …). */
export type StringTable = Readonly<Record<string, string>>;

const english: StringTable = en;
const tables = new Map<LanguageCode, StringTable>([['en', english]]);
// English is bundled above, so it is left out of the lazy chunks.
const loaders = import.meta.glob<StringTable>(['./locales/*.json', '!./locales/en.json'], { import: 'default' });

/** Active UI language. Set it with `setLocale` (which loads the table first), never directly. */
export const locale = signal<LanguageCode>('en');

/** BCP 47 tag for Intl formatting: the UI language plus the user's region when known (e.g. 'de-CH', 'en-GB'). */
export const formatLocale = signal<string>('en');

export const isRTL = (code: LanguageCode): boolean => supportedLanguages.some((l) => l.code === code && l.rtl);

/** 'rtl' for Arabic and Hebrew. */
export const direction = computed<'ltr' | 'rtl'>(() => (isRTL(locale.value) ? 'rtl' : 'ltr'));

// MARK: - Lookup

const reportedMissing = new Set<string>();

/**
 * Text for a key that no table has. Development: the key itself, logged once, so the gap is visible. Production:
 * nothing — a raw key is never shown to a hiker. (Every generated key exists in every locale file, and `t` only
 * accepts known keys; a gap can only come from `translate` with a key read at run time.)
 */
export function missingKeyText(key: string, dev: boolean = import.meta.env.DEV): string {
  if (!dev) return '';
  if (!reportedMissing.has(key)) {
    reportedMissing.add(key);
    console.warn(`[i18n] missing string '${key}'`);
  }
  return key;
}

/** True when the key exists (in English, hence in every language); narrows a runtime string to `L10nKey`. */
export function hasKey(key: string): key is L10nKey {
  return key in english;
}

/**
 * Localized text for `key`, with `args` substituted into its placeholders. The active language's table is used,
 * English when the key is missing there. Without arguments the text is returned verbatim, as iOS shows a string
 * without interpolations as is ("15 %" stays "15 %").
 *
 * Keys and argument counts are checked by the compiler (src/i18n/keys.ts). Arguments go in the order of the
 * interpolations of the Swift default value, which the tuple labels name: "\(d) from \(name)" → t(key, d, name).
 */
export function t<K extends L10nKey>(key: K, ...args: L10nArgs[K]): string {
  return translate(key, args);
}

/** `t` for a key only known at run time (e.g. read from data); unknown keys behave as in `t`. */
export function translate(key: string, args: readonly FormatArg[] = []): string {
  const template = tables.get(locale.value)?.[key] ?? english[key];
  if (template === undefined) return missingKeyText(key);
  return args.length ? formatString(template, args, formatLocale.value) : template;
}

// MARK: - Placeholders (Foundation String(format:) subset used by the catalog)

/** %@, %1$@, %lld, %ld, %d, %i, %u, %llu, %lu, %f, %lf, %.2f, %2$.1f and %%. */
const PLACEHOLDER = /%(?:(\d+)\$)?(?:\.(\d+))?(lld|llu|ld|lu|lf|@|d|i|u|f|%)/g;

const numberFormats = new Map<string, Intl.NumberFormat>();

function numberFormat(localeTag: string, minFraction: number, maxFraction: number): Intl.NumberFormat {
  const id = `${localeTag}|${minFraction}|${maxFraction}`;
  let format = numberFormats.get(id);
  if (!format) {
    format = new Intl.NumberFormat(localeTag, {
      minimumFractionDigits: minFraction,
      maximumFractionDigits: maxFraction,
    });
    numberFormats.set(id, format);
  }
  return format;
}

/**
 * Substitutes `args` into an iOS format string. Positional (`%2$@`) and sequential placeholders may be mixed as in
 * Foundation; numbers are formatted in `localeTag` (grouping, decimal separator), like `String(localized:)` does.
 */
export function formatString(
  template: string,
  args: readonly FormatArg[],
  localeTag: string = formatLocale.peek(),
): string {
  let next = 0;
  return template.replace(PLACEHOLDER, (match, position?: string, precision?: string, type = '@') => {
    if (type === '%') return position || precision ? match : '%';
    const index = position ? Number(position) - 1 : next++;
    if (index < 0 || index >= args.length) {
      if (import.meta.env.DEV) console.warn(`[i18n] no argument for ${match} in '${template}'`);
      return '';
    }
    const arg = args[index];
    if (typeof arg === 'string') return arg;
    if (!Number.isFinite(arg)) return String(arg);
    switch (type) {
      case '@':
        return numberFormat(localeTag, 0, 20).format(arg);
      case 'f':
      case 'lf': {
        const digits = precision === undefined ? 6 : Number(precision);
        return numberFormat(localeTag, digits, digits).format(arg);
      }
      default:
        return numberFormat(localeTag, 0, 0).format(Math.trunc(arg));
    }
  });
}

// MARK: - Language detection

const byLowerCode = new Map<string, LanguageCode>(supportedLanguages.map((l) => [l.code.toLowerCase(), l.code]));

/**
 * The supported language for one BCP 47 tag, or undefined. Exact codes first; then Portuguese → pt-PT unless
 * Brazil, Chinese → Traditional for Hant/TW/HK/MO else Simplified, Norwegian (no, nn, nb) → nb, legacy iw → he and
 * in → id, otherwise the base language ('de-AT' → de, 'en-US' → en).
 */
export function matchLanguage(tag: string): LanguageCode | undefined {
  const lower = tag.trim().replaceAll('_', '-').toLowerCase();
  if (!lower) return undefined;
  const exact = byLowerCode.get(lower);
  if (exact) return exact;
  const [language, ...rest] = lower.split('-');
  switch (language) {
    case 'pt':
      return rest.includes('br') ? 'pt-BR' : 'pt-PT';
    case 'zh':
      if (rest.includes('hans')) return 'zh-Hans';
      if (rest.some((s) => s === 'hant' || s === 'tw' || s === 'hk' || s === 'mo')) return 'zh-Hant';
      return 'zh-Hans';
    case 'no':
    case 'nn':
    case 'nb':
      return 'nb';
    case 'iw':
      return 'he';
    case 'in':
      return 'id';
    default:
      return byLowerCode.get(language);
  }
}

/** The browser's preferred languages (empty outside a browser). */
export function browserLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  if (navigator.languages?.length) return navigator.languages;
  return navigator.language ? [navigator.language] : [];
}

/** First supported language in the user's preference order; English when none is supported. */
export function detectLanguage(preferred: readonly string[] = browserLanguages()): LanguageCode {
  for (const tag of preferred) {
    const match = matchLanguage(tag);
    if (match) return match;
  }
  return 'en';
}

/** The user's own choice when it is a supported code (persisted by the caller), else the detected language. */
export function resolveLanguage(
  override: string | null | undefined,
  preferred: readonly string[] = browserLanguages(),
): LanguageCode {
  const chosen = override ? byLowerCode.get(override.toLowerCase()) : undefined;
  return chosen ?? detectLanguage(preferred);
}

function regionOf(tag: string): string | undefined {
  try {
    return new Intl.Locale(tag.replaceAll('_', '-')).region;
  } catch {
    return undefined;
  }
}

/**
 * Tag for numbers, dates and units: the UI language with the region of the user's preferred languages — a tag of the
 * same language first, else the first tag with a region (iOS: app language + device region). 'pt-PT' / 'pt-BR' keep
 * their own region.
 */
export function formattingLocaleFor(code: LanguageCode, preferred: readonly string[] = browserLanguages()): string {
  if (regionOf(code)) return code;
  const sameLanguage = preferred.filter((p) => matchLanguage(p) === code);
  const region = sameLanguage.map(regionOf).find(Boolean) ?? preferred.map(regionOf).find(Boolean);
  if (!region) return code;
  try {
    return Intl.getCanonicalLocales(`${code}-${region}`)[0] ?? code;
  } catch {
    return code;
  }
}

// MARK: - Loading

/** The table of a language, downloading it on first use (English is bundled). */
export async function loadLocale(code: LanguageCode): Promise<StringTable> {
  const cached = tables.get(code);
  if (cached) return cached;
  const loader = loaders[`./locales/${code}.json`];
  if (!loader) throw new Error(`No string table for '${code}'`);
  const table = await loader();
  tables.set(code, table);
  return table;
}

let switchRequest = 0;

/**
 * Loads `code` and makes it the active language; sets <html lang dir>. When calls overlap, the last one wins. If the
 * table cannot be loaded (offline before it was cached) the current language stays and the error is rethrown.
 */
export async function setLocale(code: LanguageCode, preferred: readonly string[] = browserLanguages()): Promise<void> {
  const request = ++switchRequest;
  await loadLocale(code);
  if (request !== switchRequest) return;
  batch(() => {
    locale.value = code;
    formatLocale.value = formattingLocaleFor(code, preferred);
  });
  if (typeof document !== 'undefined') {
    document.documentElement.lang = code;
    document.documentElement.dir = isRTL(code) ? 'rtl' : 'ltr';
  }
}

/** Startup: the persisted override (or null) → detected language → loaded and applied. Returns the language used. */
export async function initI18n(
  override?: string | null,
  preferred: readonly string[] = browserLanguages(),
): Promise<LanguageCode> {
  const code = resolveLanguage(override, preferred);
  try {
    await setLocale(code, preferred);
    return code;
  } catch {
    await setLocale('en', preferred);
    return 'en';
  }
}
